import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "tailwindcss/tailwind.css";

export default function Faqs() {
  const [openIndex, setOpenIndex] = useState(null);
  const contentRefs = useRef([]);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const toggleAccordion = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  useEffect(() => {
    contentRefs.current = contentRefs.current.slice(0, data.length);
  }, [data]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="relative faqs-section py-20" id="faq-section" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h1 id="faqs" className="text-[28px] md:text-[42px] lg:text-[56px] font-bold mb-4">
            FAQs
          </h1>
        </motion.div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {data.map((item, index) => (
            <motion.div 
              key={index} 
              className="border rounded-lg border-[#A3A4A7] mb-4 hover:shadow-md transition-shadow duration-300"
              variants={itemVariants}
            >
            <button
              className="flex justify-between items-center p-4 w-full text-left focus:outline-none"
              onClick={() => toggleAccordion(index)}
            >
              <span className="text-[14px] md:text-[20px] font-semibold">
                {item.name}
              </span>
              {openIndex === index ? (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 11H15M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
                    stroke="#071A34"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                    stroke="#071A34"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              )}
            </button>
            <div
              ref={(el) => (contentRefs.current[index] = el)}
              style={{
                maxHeight:
                  openIndex === index
                    ? `${contentRefs.current[index]?.scrollHeight}px`
                    : "0px",
              }}
              className="overflow-hidden transition-max-height duration-300"
            >
              <div className="text-[#667085] text-[14px] md:text-[20px] pt-2 p-4">
                {item.detail}
              </div>
            </div>
            </motion.div>
          ))}
        </motion.div>
        {/* <span className="flex gap-1  items-center justify-center mx-auto text-[12px] md:text-[15px] max-w-[500px] text-gray-500 mt-6 px-4 rounded-full bg-[#F2FCDA]  h-[36px]">
          Can’t find the answer you’re looking for?
          <a
            href="#"
            className="text-[#8ABB01] text-[10px] md:text-[15px]  hover:underline"
          >
            Get in touch with us.
          </a>
          <svg
            width="17"
            height="16"
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.105 4.75L13.605 8M13.605 8L10.105 11.25M13.605 8H3.10498"
              stroke="#8ABB01"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span> */}
      </div>
    </section>
  );
}

const data = [
  {
    name: "What is Nudgie?",
    detail:
      "Nudgie is an AI-powered accountability coach that helps you break down your goals into a clear, actionable plan and then holds you accountable to it through personalized text messages. Think of it as having a personal coach in your pocket that guides you every step of the way.",
  },
  {
    name: "How does the app create a plan for my goals?",
    detail:
      "Nudgie kicks things off with an onboarding chat, similar to a session with a human coach. Here, our AI learns about your motivations, availability, and goals. It then designs a personalized plan featuring habits, milestones, and metrics. Whether refining your existing plan or starting anew, Nudgie customizes its approach to fit your needs.",
  },
  {
    name: "How do I get started with Nudgie?",
    detail:
      "To get started, simply sign up to be part of our early access group. You can do this by providing your email address at the top of our homepage. We'll send you an invitation to access the app, and you can begin setting your goals right away!",
  },
  {
    name: "So how much does this all cost?",
    detail:
      "Nudgie is currently free as we are in the beta phase. We want to build a strong user base and fine-tune our features with real user feedback before considering any pricing.",
  },
  {
    name: "How does Nudgie differ from other productivity apps?",
    detail:
      "Nudgie is not just a tool - it’s a coach with a philosophy grounded in the principles of effective goal achievement, based on extensive experience in productivity enhancement. Unlike apps that passively track tasks, Nudgie actively engages with you, pushing for accountability and requiring you to justify missed tasks.",
  },
  {
    name: "What happens if I want to adjust my goals or plans?",
    detail:
      " Nudgie understands that life can be unpredictable and is designed to adapt to the natural volatility of your daily schedule. However, while Nudgie is flexible, it also emphasizes the importance of honoring your commitments to yourself and is strict about making sure you don't get into a habit of changing or breaking your promises to yourself.",
  },
  {
    name: "Can I use Nudgie for any type of goal?",
    detail:
      "Absolutely! Whether it's fitness, education, or organizing a neighborhood cat parade, Nudgie is equipped to help you succeed in any task.",
  },
];
